/**
 * Tiempo activo (contrato 3.1, `heartbeat.active_ms`, `active_on_step_ms`):
 * cada interacción con la pestaña visible cuenta los 10 s siguientes; las
 * ventanas que se solapan se unen (dos clics a 3 s son 13 s, no 20). Al
 * ocultarse la pestaña, la ventana abierta se corta en ese momento.
 *
 * Es un reloj puro: recibe las horas desde fuera, no lee el reloj ni usa
 * temporizadores. Quien lo usa guarda el total en un momento y resta (latido,
 * paso actual).
 */

export const ACTIVE_WINDOW_MS = 10_000;

export class ActivityClock {
  private readonly windowMs: number;
  /** ms activos de las ventanas ya cerradas. */
  private closed = 0;
  /** Ventana abierta: [start, end), o null si no hay. */
  private start: number | null = null;
  private end = 0;

  constructor(windowMs: number = ACTIVE_WINDOW_MS) {
    this.windowMs = windowMs;
  }

  /** Interacción con la pestaña visible en `t`: abre o alarga la ventana hasta `t + 10 s`. */
  interact(t: number): void {
    if (this.start !== null && t <= this.end) {
      this.end = Math.max(this.end, t + this.windowMs);
      return;
    }
    this.close();
    this.start = t;
    this.end = t + this.windowMs;
  }

  /** La pestaña se oculta (o la página se va) en `t`: la ventana abierta acaba ahí. */
  stop(t: number): void {
    if (this.start === null) return;
    this.end = Math.max(this.start, Math.min(this.end, t));
    this.close();
  }

  /** ms activos acumulados hasta `t` (incluida la parte ya vivida de la ventana abierta). */
  totalAt(t: number): number {
    if (this.start === null) return this.closed;
    return this.closed + Math.max(0, Math.min(this.end, t) - this.start);
  }

  private close(): void {
    if (this.start === null) return;
    this.closed += Math.max(0, this.end - this.start);
    this.start = null;
  }
}
